<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-vue-next';
import {
    currentUserLat, currentUserLng,
} from '@deliveryteam-spa-orders-vue/refs/UserCurrentLocation';
import { useMyOrdersMap } from '@deliveryteam-spa-orders-vue/composables/useMyOrdersMap';
import GMap from './GMap.vue';
import SimpleMap from './BasicMap.vue';
import { ref, watch } from 'vue';

const MY_ORDERS_MAP_MODE_KEY = 'COURIER_MAP_MODE_V1';

export type MyOrdersMapMode = 'none' | 'basic' | 'google';

function readMyOrdersMapModeFromStorage(): MyOrdersMapMode {
    try {
        const v = localStorage.getItem(MY_ORDERS_MAP_MODE_KEY);
        if (v === 'none' || v === 'basic' || v === 'google') return v;
    } catch {
        /* ignore */
    }
    return 'none';
}

const { googleDirectionsLinkUrl } = useMyOrdersMap(currentUserLat, currentUserLng);

const mapMode = ref<MyOrdersMapMode>(readMyOrdersMapModeFromStorage());

watch(mapMode, (v) => {
    try {
        localStorage.setItem(MY_ORDERS_MAP_MODE_KEY, v);
    } catch {
        /* ignore */
    }
});

</script>

<template>
    <div
         class="flex justify-between items-center align-middle max-w-full overflow-x-auto overflow-y-hidden whitespace-nowrap">
        <div class="bg-muted text-muted-foreground w-fit flex items-center justify-center rounded-lg p-1 gap-2 my-1">
            <Button variant="ghost"
                    @click="mapMode = 'none'"
                    :class="mapMode === 'none' ? 'bg-background text-foreground shadow hover:bg-background/80' : ''"
                    size="sm">
                None
            </Button>
            <Button variant="ghost"
                    @click="mapMode = 'basic'"
                    :class="mapMode === 'basic' ? 'bg-background text-foreground shadow hover:bg-background/80' : ''"
                    size="sm">
                BasicMap
            </Button>
            <Button variant="ghost"
                    @click="mapMode = 'google'"
                    :class="mapMode === 'google' ? 'bg-background text-foreground shadow hover:bg-background/80' : ''"
                    size="sm">
                GoogleMap
            </Button>
        </div>
        <slot></slot>
    </div>
    <div class="w-full border border-border overflow-hidden my-2">
        <a v-if="googleDirectionsLinkUrl"
           target="_blank"
           rel="noopener noreferrer"
           class="z-10 bg-background/80 p-3 min-w-full text-center text-sm border-t border-border block"
           :href="googleDirectionsLinkUrl">
            <ExternalLink class="mr-2 inline-block h-4 w-4" />Open in Google Maps
        </a>
        <GMap v-if="mapMode === 'google'" />
        <SimpleMap v-if="mapMode === 'basic'" />
    </div>
</template>
